const themeName = {
	very: 'very',
	littlewoods: 'littlewoods',
};

const fontFamily = {
	[themeName.very]: "'Very Sans'",
	[themeName.littlewoods]: "'Very Sans'",
};

const convertRemToPx = (val) => {
	const sanitisedVal = val.includes('rem') ? val.replace('rem', '') : val;

	return sanitisedVal * 16;
};

const defaultCssProps = {
	m3: {
		fontSize: `${10 / 16}rem`,
		lineHeight: `${14 / 16}rem`,
		marginBottom: `var(--spacing-global-space3)`,
	},
	m2: {
		fontSize: `${12 / 16}rem`,
		lineHeight: `${16 / 16}rem`,
		marginBottom: `var(--spacing-global-space3)`,
	},
	m1: {
		fontSize: `${14 / 16}rem`,
		lineHeight: `${20 / 16}rem`,
		marginBottom: `var(--spacing-global-space3)`,
	},
	t1: {
		fontSize: `${16 / 16}rem`,
		lineHeight: `${24 / 16}rem`,
		marginBottom: `var(--spacing-global-space4)`,
	},
	t2: {
		fontSize: `${18 / 16}rem`,
		lineHeight: `${26 / 16}rem`,
		marginBottom: `var(--spacing-global-space4)`,
	},
	t3: {
		fontSize: `${22 / 16}rem`,
		lineHeight: `${32 / 16}rem`,
		marginBottom: `var(--spacing-global-space5)`,
	},
	t4: {
		fontSize: `${25 / 16}rem`,
		lineHeight: `${36 / 16}rem`,
		marginBottom: `var(--spacing-global-space5)`,
	},
	t5: {
		fontSize: `${30 / 16}rem`,
		lineHeight: `${40 / 16}rem`,
		marginBottom: `var(--spacing-global-space6)`,
	},
	t6: {
		fontSize: `${34 / 16}rem`,
		lineHeight: `${48 / 16}rem`,
		marginBottom: `var(--spacing-global-space6)`,
	},
	t7: {
		fontSize: `${40 / 16}rem`,
		lineHeight: `${52 / 16}rem`,
		marginBottom: `var(--spacing-global-space7)`,
	},
	t8: {
		fontSize: `${45 / 16}rem`,
		lineHeight: `${56 / 16}rem`,
		marginBottom: `var(--spacing-global-space7)`,
	},
	t9: {
		fontSize: `${52 / 16}rem`,
		lineHeight: `${64 / 16}rem`,
		marginBottom: `var(--spacing-global-space8)`,
	},
	t10: {
		fontSize: `${60 / 16}rem`,
		lineHeight: `${80 / 16}rem`,
		marginBottom: `var(--spacing-global-space8)`,
	},
	t11: {
		fontSize: `${70 / 16}rem`,
		lineHeight: `${88 / 16}rem`,
		marginBottom: `var(--spacing-global-space9)`,
	},
	t12: {
		fontSize: `${82 / 16}rem`,
		lineHeight: `${104 / 16}rem`,
		marginBottom: `calc(${56 / 16}rem * 1.6)` /* this is a custom value the designers specced */,
	},
	t13: {
		fontSize: `${95 / 16}rem`,
		lineHeight: `${120 / 16}rem`,
		marginBottom: `var(--spacing-global-space10)`,
	},
	t14: {
		fontSize: `${110 / 16}rem`,
		lineHeight: `${144 / 16}rem`,
		marginBottom: `calc(${80 / 16}rem * 1.6)` /* this is a custom value the designers specced */,
	},
};

const cssProps = {
	[themeName.very]: {
		...defaultCssProps,
	},
	[themeName.littlewoods]: {
		...defaultCssProps,
	},
};

const display = (themeBrand = themeName.very) => {
	// Check that theme is a valid value, if not set it as very as default
	const theme = Object.keys(themeName).includes(themeBrand) ? themeBrand : themeName.very;

	return {
		display1: cssProps[theme]?.t1,
		display2: cssProps[theme]?.t2,
		display3: cssProps[theme]?.t3,
		display4: cssProps[theme]?.t4,
		display5: cssProps[theme]?.t5,
		display6: cssProps[theme]?.t6,
		display7: cssProps[theme]?.t7,
		display8: cssProps[theme]?.t8,
		display9: cssProps[theme]?.t9,
		display10: cssProps[theme]?.t10,
		display11: cssProps[theme]?.t11,
		display12: cssProps[theme]?.t12,
		display13: cssProps[theme]?.t13,
		display14: cssProps[theme]?.t14,
		fontColor: 'var(--color-global-black)',
		fontWeight: '800', // referenced as 400 in Figma
	};
};

const heading = (themeBrand = themeName.very) => {
	// Check that theme is a valid value, if not set it as very as default
	const theme = Object.keys(themeName).includes(themeBrand) ? themeBrand : themeName.very;

	return {
		headingM3: cssProps[theme]?.m3,
		headingM2: cssProps[theme]?.m2,
		headingM1: cssProps[theme]?.m1,
		heading1: cssProps[theme]?.t1,
		heading2: cssProps[theme]?.t2,
		heading3: cssProps[theme]?.t3,
		heading4: cssProps[theme]?.t4,
		heading5: cssProps[theme]?.t5,
		heading6: cssProps[theme]?.t6,
		heading7: cssProps[theme]?.t7,
		heading8: cssProps[theme]?.t8,
		heading9: cssProps[theme]?.t9,
		heading10: cssProps[theme]?.t10,
		heading11: cssProps[theme]?.t11,
		heading12: cssProps[theme]?.t12,
		heading13: cssProps[theme]?.t13,
		heading14: cssProps[theme]?.t14,
		fontColor: 'var(--color-global-black)',
		fontWeight: '700',
	};
};

const paragraph = (themeBrand = themeName.very) => {
	// Check that theme is a valid value, if not set it as very as default
	const theme = Object.keys(themeName).includes(themeBrand) ? themeBrand : themeName.very;

	return {
		paragraphM3: cssProps[theme]?.m3,
		paragraphM2: cssProps[theme]?.m2,
		paragraphM1: cssProps[theme]?.m1,
		paragraph1: cssProps[theme]?.t1,
		paragraph2: cssProps[theme]?.t2,
		paragraph3: cssProps[theme]?.t3,
		paragraph4: cssProps[theme]?.t4,
		paragraph5: cssProps[theme]?.t5,
		paragraph6: cssProps[theme]?.t6,
		paragraph7: cssProps[theme]?.t7,
		paragraph8: cssProps[theme]?.t8,
		paragraph9: cssProps[theme]?.t9,
		paragraph10: cssProps[theme]?.t10,
		paragraph11: cssProps[theme]?.t11,
		paragraph12: cssProps[theme]?.t12,
		paragraph13: cssProps[theme]?.t13,
		paragraph14: cssProps[theme]?.t14,
		fontColor: 'var(--color-global-grey900)',
		fontWeight: '400', // referenced as 500 in Figma
	};
};

export { convertRemToPx, display, heading, paragraph, fontFamily, themeName, defaultCssProps };
